// WeeklyReportView.js — 주간 경영관리 리포트를 웹 화면에서 바로 보는 전체화면 오버레이.
// PurchasePlanHistoryView / SalesByVendorView와 동일한 패턴(fixed inset-0, 조회 전용).
// 재고 상태·발주점(ROP)은 App.js가 이미 계산한 items/reorderRows를 그대로 받아 재사용하고,
// 여기서는 리포트에만 필요한 급증/지연/미출고·현금흐름·벤더 리드타임 실측만 추가로 계산한다.
// (이메일 자동발송판 functions/weeklyReport.js와 판정 기준을 동일하게 유지해야 한다.)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, RefreshCw, TrendingUp, AlertTriangle, PackageSearch } from "lucide-react";
import Chart from "chart.js/auto";
import { fetchProcurementPlans, monthlyPaymentForecast, summarizePlans } from "./procurementPlans";
import { fetchSalesRecords } from "./ecountSales";
import { fetchPurchaseRecords } from "./purchaseTaxInvoice";
import { fetchQuotations, summarizeQuotations } from "./quotations";
import {
  computeSurgeItems, computeStagnantItems, computeNoOutboundItems,
  computeValueCompare, computeDIO, computeVendorLeadTimes, buildMonthlyCashflow,
  computeProcurementWeekly, ymd, addDays,
} from "./weeklyReportCalc";

const won = (n) => (n == null ? "—" : "₩" + Math.round(Number(n)).toLocaleString("ko-KR"));
const nf = (n) => (n == null ? "—" : Number(n).toLocaleString("ko-KR"));
const pctStr = (n) => (n == null ? "—" : (n >= 0 ? "▲ " : "▼ ") + Math.abs(n).toFixed(1) + "%");

function ChartBox({ config, height = 220 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const c = new Chart(ref.current, config);
    return () => c.destroy();
  }, [config]);
  return <div style={{ height }} className="relative"><canvas ref={ref} /></div>;
}

const SummaryCard = ({ label, value, sub, tone }) => (
  <div className={"rounded-xl p-4 shadow-sm text-white " + (tone === "red" ? "bg-red-600" : tone === "amber" ? "bg-amber-600" : tone === "green" ? "bg-emerald-700" : "bg-slate-900")}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-white/70 mb-1.5">{label}</div>
    <div className="text-xl font-semibold tabular-nums leading-tight">{value}</div>
    {sub && <div className="text-[11px] text-white/70 mt-0.5">{sub}</div>}
  </div>
);

const SectionTitle = ({ children }) => <h3 className="text-sm font-bold text-slate-800 border-b-2 border-teal-700 pb-1.5 mt-6 mb-3">{children}</h3>;

function DataTable({ headers, rows, empty = "해당 없음" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto mb-4">
      <table className="w-full text-xs">
        <thead><tr className="bg-slate-800 text-white">{headers.map((h) => <th key={h} className="text-left font-semibold px-3 py-2 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>
          {rows.length ? rows : <tr><td colSpan={headers.length} className="text-center text-slate-400 py-6">{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
const Td = ({ children, right, color, bold }) => (
  <td className={"px-3 py-2 border-t border-slate-100 whitespace-nowrap " + (right ? "text-right tabular-nums" : "") + (bold ? " font-semibold" : "")} style={color ? { color } : undefined}>{children}</td>
);

export default function WeeklyReportView({ onClose, items, dailySalesByCode, reorderRows, leadTimes, valueHistory, totals, statusCounts, otherInflows = [] }) {
  const [plans, setPlans] = useState(null);
  const [salesRecords, setSalesRecords] = useState(null);
  const [purchaseRecords, setPurchaseRecords] = useState(null);
  const [quotes, setQuotes] = useState({ source: "", list: [], error: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const [p, s, pi, q] = await Promise.all([
        fetchProcurementPlans(),
        fetchSalesRecords(200),
        fetchPurchaseRecords(200).catch(() => []), // 매입세금계산서 미업로드/권한 미설정이어도 리포트는 나와야 함
        fetchQuotations().catch(() => ({ source: "", list: [], error: "" })), // 견적서(단가마스터 앱) 조회 실패해도 리포트는 나와야 함
      ]);
      setPlans(p); setSalesRecords(s); setPurchaseRecords(pi); setQuotes(q);
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const today = ymd(new Date());
  const periodEnd = addDays(today, -1);
  const periodStart = addDays(periodEnd, -6);
  const periodLabel = `${periodStart} ~ ${periodEnd} (기준일 ${today})`;

  const surgeItems = useMemo(() => computeSurgeItems(dailySalesByCode, items, { today }), [dailySalesByCode, items, today]);
  const stagnantItems = useMemo(() => computeStagnantItems(dailySalesByCode, items, { today }), [dailySalesByCode, items, today]);
  const noOutboundItems = useMemo(() => computeNoOutboundItems(dailySalesByCode, items, { today }), [dailySalesByCode, items, today]);
  const valueCompare = useMemo(() => computeValueCompare(valueHistory, totals.value, today), [valueHistory, totals.value, today]);
  const dio = useMemo(() => computeDIO(items, totals.value), [items, totals.value]);
  const vendorLeadTimes = useMemo(() => computeVendorLeadTimes(plans || []), [plans]);
  const monthlyForecast = useMemo(() => monthlyPaymentForecast(plans || []), [plans]);
  // 매출매입 화면(현금흐름 탭)과 동일한 기준으로 계산한다 —
  // 매출입금: 쿠팡 70%@+60일·30%@+90일, 그 외 거래처 +3일 / 매입출금: 구매계획 결제조건(거래처 덮어쓰기 포함)
  // + 매입세금계산서(매입월 말일+10일) / 기타 입금(대출·보조금)까지 합산. (2026-07-25 통일)
  const cashflow = useMemo(() => buildMonthlyCashflow({
    salesRecords: salesRecords || [], monthlyForecast, purchaseInvoiceRecords: purchaseRecords || [],
    today, monthsAhead: 5, otherInflows,
  }), [salesRecords, monthlyForecast, purchaseRecords, today, otherInflows]);

  // 발주 시급: App.js가 이미 계산한 reorderRows 중 '여유'가 아닌 것만, 그리고 리드타임이
  // 입력된 품목만(미입력 품목은 임의 기본값으로 추정하지 않고 제외 — 2026-07-24 확정).
  const urgentRows = useMemo(() => (reorderRows || []).filter((r) => r.signal !== "여유" && leadTimes[r.code] != null && leadTimes[r.code] !== ""), [reorderRows, leadTimes]);
  const skippedNoLeadTime = useMemo(() => (reorderRows || []).filter((r) => r.signal !== "여유" && (leadTimes[r.code] == null || leadTimes[r.code] === "")).length, [reorderRows, leadTimes]);

  const confirmedThisWeek = useMemo(() => (plans || []).filter((p) => { const c = p.confirmedAt ? String(p.confirmedAt).slice(0, 10) : null; return c && c >= periodStart && c <= periodEnd; }), [plans, periodStart, periodEnd]);
  const planSummary = useMemo(() => summarizePlans(confirmedThisWeek), [confirmedThisWeek]);

  // 이번 주 입고(구매계획 완료) · 발주서 발행 — 발주 계획 앱의 확정 금액을 그대로 집계한다.
  const proc = useMemo(() => computeProcurementWeekly(plans || [], { periodStart, periodEnd, today }), [plans, periodStart, periodEnd, today]);
  const quoteSummary = useMemo(() => summarizeQuotations(quotes.list, { periodStart, periodEnd }), [quotes, periodStart, periodEnd]);

  const risks = [];
  if (noOutboundItems.length) risks.push(`30일 이상 미출고 ${noOutboundItems.length}건 (재고금액 ${won(noOutboundItems.reduce((s, x) => s + (x.value || 0), 0))})`);
  if (urgentRows.length) risks.push(`발주 시급(지금발주/곧발주) ${urgentRows.length}건`);
  if (proc.delayed.count) risks.push(`선적예정일 경과 미완료 발주 ${proc.delayed.count}건 (${won(proc.delayed.krw)})`);
  const firstNegative = cashflow.base.find((m) => m.cumulative < 0);
  if (firstNegative) risks.push(`${firstNegative.month} 누적 현금흐름 음수 전환 예상 (${won(firstNegative.cumulative)})`);
  if (!risks.length) risks.push("이번 주 특이 리스크 없음");

  const cashflowChartConfig = useMemo(() => ({
    type: "line",
    data: {
      labels: cashflow.base.map((m) => m.month),
      datasets: [
        { label: "누적 현금흐름(기본)", data: cashflow.base.map((m) => m.cumulative), borderColor: "#0F5C6B", backgroundColor: "rgba(15,92,107,0.08)", fill: true, tension: 0.25 },
        { label: "누적 현금흐름(보수적, +2주 지연)", data: cashflow.conservative.map((m) => m.cumulative), borderColor: "#B3261E", borderDash: [5, 4], fill: false, tension: 0.25 },
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } }, tooltip: { callbacks: { label: (c) => ` ${c.dataset.label}: ${won(c.raw)}` } } },
      scales: { y: { ticks: { callback: (v) => won(v) } } },
    },
  }), [cashflow]);

  return (
    <div className="fixed inset-0 z-[9999] bg-white overflow-auto">
      <div className="max-w-[1440px] mx-auto p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-y-2">
          <div className="flex items-center gap-2">
            <PackageSearch className="h-5 w-5 text-teal-700" />
            <h2 className="text-xl font-bold text-slate-800">주간 경영관리 리포트</h2>
            <span className="text-xs text-slate-400 ml-1">{periodLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all disabled:opacity-50">
              <RefreshCw className={"h-4 w-4" + (loading ? " animate-spin" : "")} />새로고침
            </button>
            <button onClick={onClose} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all">
              <X className="h-4 w-4" />닫기
            </button>
          </div>
        </div>

        {err && <p className="text-red-600 text-sm mb-3">발주 계획/매출 데이터 조회 오류: {err} (재고·발주 시급 섹션은 정상 표시됩니다)</p>}

        {/* Executive Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <SummaryCard label="총 재고금액" value={won(totals.value)} sub={pctStr(valueCompare.pct) + (valueCompare.priorDate ? ` (${valueCompare.priorDate} 대비)` : " (비교 데이터 없음)")} tone={valueCompare.pct < 0 ? "red" : "green"} />
          <SummaryCard label="부족 / 과잉 재고" value={`${nf(statusCounts.부족)} / ${nf(statusCounts.과잉)}건`} sub="부족 · 과잉" tone={statusCounts.부족 ? "red" : "green"} />
          <SummaryCard label="30일 이상 미출고" value={`${nf(noOutboundItems.length)}건`} sub={won(noOutboundItems.reduce((s, x) => s + (x.value || 0), 0))} tone={noOutboundItems.length ? "amber" : "green"} />
          <SummaryCard label="발주 시급(지금/곧)" value={`${nf(urgentRows.length)}건`} sub={skippedNoLeadTime ? `리드타임 미입력 ${skippedNoLeadTime}건 제외` : "리드타임 입력 품목 기준"} tone={urgentRows.length ? "red" : "green"} />
          <SummaryCard label="이번 주 입고(구매계획 완료)" value={won(proc.inbound.krw)} sub={`${nf(proc.inbound.count)}건 · ${nf(proc.inbound.vendors)}개사` + (proc.delayed.count ? ` · 지연 ${nf(proc.delayed.count)}건` : "")} tone={proc.delayed.count ? "amber" : "green"} />
          <SummaryCard label="이번 주 발주서 발행" value={won(proc.issued.krw)} sub={`${nf(proc.issued.count)}건 · 수량 ${nf(proc.issued.qty)}`} />
          <SummaryCard label="재고자산회전일수(DIO)" value={dio.dio != null ? dio.dio.toFixed(1) + "일" : "—"} sub="재고금액÷일평균매출원가" />
          <SummaryCard label="현금흐름 첫 음수 전환월" value={firstNegative ? firstNegative.month : "없음"} sub="기본 시나리오 기준" tone={firstNegative ? "amber" : "green"} />
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-2">
          <div className="text-xs font-bold text-amber-700 mb-1 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Top 리스크</div>
          <ul className="text-xs text-amber-800 list-disc pl-4 space-y-0.5">{risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>

        <SectionTitle>매출 및 현금흐름 (향후 {cashflow.base.length}개월)</SectionTitle>
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 mb-4">
          {loading && !salesRecords ? <p className="text-sm text-slate-400 text-center py-8">현금흐름 데이터 불러오는 중…</p> : <ChartBox config={cashflowChartConfig} height={220} />}
        </div>
        <DataTable headers={["월", "현금유입(+)", "매출/기타", "매입지급(-)", "구매계획/실제매입", "순현금흐름", "누적(기본)", "누적(보수적)"]} rows={cashflow.base.map((m, i) => (
          <tr key={m.month}>
            <Td>{m.month}</Td>
            <Td right color="#1E7A34">{won(m.inflow)}</Td>
            <Td right color="#888">{won(m.salesInflow)} / {won(m.otherInflow)}</Td>
            <Td right color="#B3261E">{won(m.outflow)}</Td>
            <Td right color="#888">{won(m.planOutflow)} / {won(m.invoiceOutflow)}</Td>
            <Td right bold>{won(m.net)}</Td><Td right bold color={m.cumulative < 0 ? "#B3261E" : undefined}>{won(m.cumulative)}</Td>
            <Td right color="#666">{won(cashflow.conservative[i]?.cumulative)}</Td>
          </tr>
        ))} />
        <p className="text-[11px] text-slate-400 -mt-2 mb-4">
          현금유입 = 매출입금(쿠팡 70%@+60일·30%@+90일, 그 외 거래처 +3일) + 기타 입금(대출·보조금 등 매출매입 화면에 등록된 항목) ·
          매입지급 = 구매계획 결제조건(거래처별 예외 규칙 포함) + 매입세금계산서(매입월 말일+10일, 우진은 전월23일~당월22일 청구주기) ·
          누적은 기초 잔액 0원 기준 순증감 누적 · 보수적 시나리오는 매출입금만 2주 추가 지연 가정.
        </p>

        <SectionTitle>구매계획 요약 — 이번 주 입고(완료) · 발주서 발행</SectionTitle>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <SummaryCard label="이번 주 입고(완료)" value={won(proc.inbound.krw)} sub={`${nf(proc.inbound.count)}건 · 수량 ${nf(proc.inbound.qty)} · ${nf(proc.inbound.vendors)}개사`} tone="green" />
          <SummaryCard label="이번 주 발주서 발행" value={won(proc.issued.krw)} sub={`${nf(proc.issued.count)}건 · 수량 ${nf(proc.issued.qty)} · ${nf(proc.issued.vendors)}개사`} />
          <SummaryCard label="이번 주 확정 발주" value={won(planSummary.grand)} sub={`${nf(planSummary.count)}건 (확정일시 기준)`} />
          <SummaryCard label="선적예정일 경과 미완료" value={won(proc.delayed.krw)} sub={`${nf(proc.delayed.count)}건`} tone={proc.delayed.count ? "amber" : "green"} />
        </div>

        <p className="text-xs font-semibold text-slate-600 mb-1.5">이번 주 입고 내역 (구매계획 완료 · 최종 선적일 {periodStart} ~ {periodEnd})</p>
        <DataTable headers={["선적일", "거래처", "통화", "발주금액", "원화환산", "수량", "품목수", "결제조건"]} empty="이번 주 완료 처리된 구매계획이 없습니다" rows={proc.inbound.rows.map((p) => (
          <tr key={p.id}>
            <Td>{p.date}</Td><Td>{p.vendor}</Td><Td right>{p.cur}</Td>
            <Td right>{p.cur === "$" ? "$" + p.totAmt.toLocaleString("en-US", { maximumFractionDigits: 2 }) : won(p.totAmt)}</Td>
            <Td right bold color="#1E7A34">{won(p.krw)}</Td><Td right>{nf(p.totQty)}</Td><Td right>{nf(p.itemCount)}</Td><Td>{p.payTerm || "—"}</Td>
          </tr>
        ))} />

        <p className="text-xs font-semibold text-slate-600 mb-1.5">이번 주 발주서 발행 내역 (발행일 {periodStart} ~ {periodEnd})</p>
        <DataTable headers={["발행일", "거래처", "통화", "발주금액", "원화환산", "수량", "선적예정", "상태"]} empty="이번 주 발행된 발주서가 없습니다" rows={proc.issued.rows.map((p) => (
          <tr key={p.id}>
            <Td>{p.issueDate}</Td><Td>{p.vendor}</Td><Td right>{p.cur}</Td>
            <Td right>{p.cur === "$" ? "$" + p.totAmt.toLocaleString("en-US", { maximumFractionDigits: 2 }) : won(p.totAmt)}</Td>
            <Td right bold>{won(p.krw)}</Td><Td right>{nf(p.totQty)}</Td><Td right>{p.date || "—"}</Td>
            <Td right color={p.done ? "#1E7A34" : "#9C6B00"} bold>{p.done ? "완료" : "진행중"}</Td>
          </tr>
        ))} />
        <p className="text-[11px] text-slate-400 -mt-2 mb-4">
          발주 계획 앱(purchasePlans)에 저장된 확정 금액을 그대로 집계했습니다 · 입고 = 완료(done) 체크 + 최종 선적일(date) 기준 ·
          발주서 발행 = 발행일(issueDate) 기준 · 외화는 발주서에 입력된 환율(rate)로 원화 환산.
        </p>

        <SectionTitle>견적서 작성내역 (단가마스터 앱)</SectionTitle>
        {quotes.source ? (
          <>
            <p className="text-sm text-slate-600 mb-1.5">이번 주 작성 {nf(quoteSummary.weekCount)}건 · 금액 {won(quoteSummary.weekAmount)} · 거래처 {nf(quoteSummary.customers)}개사 <span className="text-slate-400">(전체 누적 {nf(quoteSummary.totalCount)}건)</span></p>
            <DataTable headers={["작성일", "견적번호", "거래처", "금액", "품목수", "상태"]} empty="이번 주 작성된 견적서가 없습니다" rows={quoteSummary.week.slice(0, 20).map((q) => (
              <tr key={q.id}><Td>{q.date || "—"}</Td><Td>{q.no}</Td><Td>{q.customer}</Td><Td right bold>{won(q.amount)}</Td><Td right>{nf(q.itemCount)}</Td><Td>{q.status || "—"}</Td></tr>
            ))} />
          </>
        ) : (
          <p className="text-xs text-slate-400 mb-4">단가마스터 앱에서 견적서 컬렉션을 찾지 못했습니다. 견적 데이터가 저장되는 Firestore 컬렉션 이름을 알려주시면 quotations.js의 CANDIDATE_COLLECTIONS 맨 앞에 넣어 바로 연결됩니다.</p>
        )}

        <SectionTitle>재고 관리 — 출고 급증 품목 (최근7일 ÷ 직전4주 ≥ 150%)</SectionTitle>
        <DataTable headers={["상품명/코드", "최근7일 평균", "직전4주 평균", "증가율", "현재고"]} rows={surgeItems.slice(0, 15).map((x) => (
          <tr key={x.code}><Td>{x.name || x.code}</Td><Td right>{nf(Math.round(x.last7))}</Td><Td right>{nf(Math.round(x.prior28))}</Td><Td right color="#B3261E" bold>{x.ratio != null ? (x.ratio * 100).toFixed(0) + "%" : x.note}</Td><Td right>{nf(x.stock)}</Td></tr>
        ))} />

        <SectionTitle>출고 지연 품목 (최근 14일 출고 0)</SectionTitle>
        <DataTable headers={["상품명/코드", "마지막 출고일", "현재고"]} rows={stagnantItems.slice(0, 15).map((x) => (
          <tr key={x.code}><Td>{x.name || x.code}</Td><Td right>{x.lastSaleDate || "-"} ({x.noSaleDays}일 전)</Td><Td right>{nf(x.stock)}</Td></tr>
        ))} />

        <SectionTitle>30일 이상 미출고 재고 (재고금액 상위 20)</SectionTitle>
        <DataTable headers={["상품명/코드", "미출고 일수", "현재고", "재고금액"]} rows={noOutboundItems.slice(0, 20).map((x) => (
          <tr key={x.code}><Td>{x.name || x.code}</Td><Td right>{x.neverSold ? "판매기록없음" : nf(x.noSaleDays) + "일"}</Td><Td right>{nf(x.stock)}</Td><Td right bold>{won(x.value)}</Td></tr>
        ))} />

        <SectionTitle>발주 시급 내역 (발주점 분석 · 리드타임 입력 품목만)</SectionTitle>
        <DataTable headers={["상품명/코드", "현재고+발주", "일평균판매", "리드타임", "발주점(ROP)", "예상소진", "판정"]} rows={urgentRows.slice(0, 30).map((r) => (
          <tr key={r.code}>
            <Td>{r.name || r.code}</Td><Td right>{nf(r.onhand)}</Td><Td right>{r.dAvg != null ? r.dAvg.toFixed(1) : "-"}</Td>
            <Td right>{nf(r.lt)}일</Td><Td right>{Math.round(r.rop)}</Td><Td right>{r.runout != null ? r.runout.toFixed(0) + "일후" : "-"}</Td>
            <Td right color={r.signal === "지금발주" ? "#B3261E" : "#9C6B00"} bold>{r.signal}</Td>
          </tr>
        ))} />
        {skippedNoLeadTime > 0 && <p className="text-xs text-slate-400 -mt-2 mb-4">리드타임 미입력 {skippedNoLeadTime}건은 제외했습니다 (발주 산출 화면에서 리드타임 입력 시 포함).</p>}

        <SectionTitle>벤더별 리드타임 실측</SectionTitle>
        <p className="text-xs text-slate-400 mb-2">발주 계획 이력의 발주일→도착예정일 실측 차이를 거래처별로 집계했습니다. 발주 산출 화면의 리드타임 입력값을 실측치로 교체하는 데 참고하세요.</p>
        <DataTable headers={["거래처", "건수", "평균 리드타임", "최소~최대"]} rows={vendorLeadTimes.slice(0, 15).map((v) => (
          <tr key={v.vendor}><Td>{v.vendor}</Td><Td right>{nf(v.count)}건</Td><Td right bold>{v.avgLT != null ? v.avgLT.toFixed(1) + "일" : "-"}</Td><Td right>{v.minLT}일 ~ {v.maxLT}일</Td></tr>
        ))} />

        <p className="text-[11px] text-slate-300 text-center mt-6 mb-2">이 화면은 브라우저에서 실시간 계산한 웹 버전입니다 · 이메일 자동발송판은 설정 화면에서 관리합니다</p>
      </div>
    </div>
  );
}
